import { studioToScreen, screenToStudio } from "@logic/lib/design"
import { selector, selectorFamily, DefaultValue, atom } from "recoil"

import type { Imagery, ImageryResult, Maybe } from "~/graphql/client"
import { createRecoilMapper, molecule } from "~/utils/recoil"

import { imageSourceState, zoomState } from "./view"

const DEFAULT_SIDE = 1024

const DEFAULT_IMAGE: IPlacedImage = {
  filename: undefined,
  imageDate: undefined,
  source: ``,
  x: 0,
  y: 0,
  w: DEFAULT_SIDE,
  h: DEFAULT_SIDE,
  isLocked: true,
  zoomLevel: 20,
}

export interface IPlacedImage {
  x: number
  y: number
  w: number
  h: number
  zoomLevel: number
  isLocked: boolean
  source: string
  filename?: Maybe<string>
  imageDate?: Maybe<string>
}

export const imageMolecule = molecule<IPlacedImage, string>({
  key: `image`,
  default: DEFAULT_IMAGE,
})

export const [findImageState, imageIndex] = imageMolecule

export const centerOfImageState = atom({
  key: `centerOfImage`,
  default: { x: 0, y: 0 },
})

export const findComputedImageState = selectorFamily<IPlacedImage, string>({
  key: `computedImage`,
  get:
    (imageId: string) =>
    ({ get }): IPlacedImage => {
      const image = get(findImageState(imageId))
      const zoom = get(zoomState)

      return {
        ...image,
        ...studioToScreen.rectangle({ ...image, r: 0 }, zoom),
      }
    },
  set:
    (imageId) =>
    ({ get, set }, newValue) => {
      if (newValue instanceof DefaultValue) {
        return console.warn(`tried to set image ${imageId} to DefaultValue`)
      }
      const zoom = get(zoomState)
      const newImage = {
        ...newValue,
        ...screenToStudio.rectangle({ ...newValue, r: 0 }, zoom),
      }
      set(findImageState(imageId), newImage as IPlacedImage)
    },
})

export const preferredImageState = selector({
  key: `preferredImage`,
  get: ({ get }) => {
    const [selected] = get(imageSourceState)

    return get(findComputedImageState(selected))
  },
})

export const preferredImageUrlState = selector({
  key: `preferredImageUrl`,
  get: ({ get }) => get(preferredImageState).filename ?? ``,
})

export const mapImageToKey = (
  image: Pick<Imagery, `createdAt` | `source`>,
): string =>
  `${image.source} (${new Date(image.createdAt).toLocaleDateString(`en-us`, {
    dateStyle: `medium`,
  })})`

export const useHydrateImages = createRecoilMapper<
  Imagery,
  IPlacedImage,
  string
>({
  makeKey: mapImageToKey,
  makeState: (result) => {
    const { height, width } = result

    const { x, y } = {
      x: (DEFAULT_SIDE - (width || DEFAULT_SIDE)) / 2,
      y: (DEFAULT_SIDE - (height || DEFAULT_SIDE)) / 2,
    }

    return {
      ...DEFAULT_IMAGE,
      ...result,
      x,
      y,
      h: height || DEFAULT_SIDE,
      w: width || DEFAULT_SIDE,
      zoomLevel: result.zoomLevel ?? 20,
    }
  },
  findState: findImageState,
  index: imageIndex,
})
